import type {
  ContingencyPlanCreateRequest,
  ContingencyPlanCreateResponse,
  ContingencyPlanDTO,
  ContingencyPlanFetchResponse,
  ContingencyPlanUpdateRequest,
  ContingencyPlanUpdateResponse
} from '@lmc/ts-common'
import { getJSON, postJSON } from 'common/services/api'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import type { ContingencyPlanExtended } from 'screens/Residents/screens/ResidentProfileScreen/screens/ResidentContingencyPlans/types'

type UseContingencyPlanProps = {
  contingencyPlanID: string | undefined
  onCreateSuccess?: () => void
  onUpdateSuccess?: () => void
  onDeleteSuccess?: () => void
}

export const GET_CONTINGENCY_PLAN = `/api/list/contingency-plans`

const useContingencyPlan = ({
  contingencyPlanID,
  onCreateSuccess,
  onUpdateSuccess,
  onDeleteSuccess
}: UseContingencyPlanProps) => {
  const queryClient = useQueryClient()

  const createFunction = (values: ContingencyPlanCreateRequest) =>
    postJSON({
      url: `list/contingency-plans`,
      method: 'POST',
      json: values
    })

  const { mutateAsync: createMutation } = useMutation<
    ContingencyPlanCreateResponse,
    unknown,
    ContingencyPlanCreateRequest
  >(['create-contingency-plan'], createFunction, {
    onSuccess: () => {
      onCreateSuccess?.()
      queryClient.invalidateQueries('contingencyPlans')
    }
  })

  const { data, isLoading: isContingencyPlanLoading } =
    useQuery<ContingencyPlanFetchResponse>(
      ['contingency-plan', contingencyPlanID],
      () => getJSON({ url: `${GET_CONTINGENCY_PLAN}/${contingencyPlanID}` }),
      {
        enabled: contingencyPlanID !== undefined,
        refetchOnMount: 'always'
      }
    )

  const updateFunction = (values: ContingencyPlanUpdateRequest) =>
    postJSON({
      url: `list/contingency-plans/${contingencyPlanID}`,
      method: 'PUT',
      json: values
    })

  const { mutateAsync: updateMutation } = useMutation<
    ContingencyPlanUpdateResponse,
    unknown,
    ContingencyPlanUpdateRequest
  >(['contingency-plan', contingencyPlanID], updateFunction, {
    onSuccess: () => {
      onUpdateSuccess?.()
      queryClient.invalidateQueries('contingency-plan')
      queryClient.invalidateQueries('contingencyPlans')
    }
  })

  const deleteFunction = () =>
    postJSON({
      url: `list/contingency-plans/${contingencyPlanID}`,
      method: 'DELETE'
    })

  const { mutateAsync: deleteMutation, isLoading: isDeleteLoading } =
    useMutation(
      ['contingency-plan', 'delete', contingencyPlanID],
      deleteFunction,
      {
        onSuccess: () => {
          onDeleteSuccess?.()
          queryClient.invalidateQueries('contingencyPlans')
        }
      }
    )

  return {
    contingencyPlan: data?.data as ContingencyPlanDTO & ContingencyPlanExtended,
    isContingencyPlanLoading,
    isDeleteLoading,
    createContingencyPlan: createMutation,
    updateContingencyPlan: updateMutation,
    deleteContingencyPlan: deleteMutation
  }
}

export default useContingencyPlan
